
import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // -------------------------------------------------
  // Scroll handling – adds a small safety check for
  // environments where `window` might be undefined
  // (e.g., SSR) and ensures the listener is always
  // removed on unmount.
  // -------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Initialise the state based on the current scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // -------------------------------------------------
  // Body‑scroll lock for the mobile menu.
  // -------------------------------------------------
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const navLinks = [
    { href: '#about', label: '会社概要' },
    { href: '#services', label: 'サービス概要' },
    { href: '/partner', label: '協力会社募集' },
    { href: '/recruit', label: '正社員募集' },
    { href: '#pricing', label: '料金プラン' },
    { href: '#contact', label: 'お問い合わせ' },
  ];

  return (
    <div className="relative min-h-screen">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 sm:gap-3 cursor-pointer group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden shadow-sm group-hover:shadow-md transition-shadow bg-white flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/0f43beb23aaca34fe525f322afdb3a4e/91f45852d6469a87479e767848617524.jpeg"
                alt="Refresh Labo ロゴ"
                className="w-full h-full object-cover"
              />
            </div>
            <span
              className="text-base sm:text-lg font-bold tracking-wide"
              style={{ color: isScrolled ? '#000' : '#fff' }}
            >
              Refresh Labo
            </span>
          </a>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium hover:opacity-70 transition-opacity cursor-pointer ${
                  isScrolled ? 'text-gray-800' : 'text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="メニュー"
          >
            <i
              className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl ${
                isScrolled ? 'text-gray-800' : 'text-white'
              }`}
            ></i>
          </button>
        </div>

        {/* Mobile menu – rendered only when open */}
        {menuOpen && (
          <div className="md:hidden fixed inset-0 top-[56px] bg-white z-40">
            <div className="flex flex-col px-6 py-6 gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-base font-medium text-gray-800 py-3 border-b border-gray-100 cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero section */}
      <div className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.wixstatic.com/media/87f369_fcd719474d274806b00edf35fd28c227~mv2.jpeg/v1/fill/w_1920,h_1080,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Untitled.jpeg"
            alt="Refresh Labo Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center text-white px-5 sm:px-6 w-full max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-5 sm:mb-8 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] leading-tight">
            ABOUT of Refresh Labo
          </h1>
          <p className="text-sm sm:text-lg md:text-2xl mb-8 sm:mb-10 leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] font-medium">
            賃貸に特化した原状回復工事をメインに、在宅、空室ハウスクリーニング・クロス張替え・床張替えなどを行うrefresh laboです。
            <br className="hidden sm:block" />
            大阪市住吉区を拠点に、幅広いお客様にご満足いただける高品質なサービス提供に努めております。
          </p>
          <a
            href="#contact"
            className="inline-block px-8 sm:px-10 py-3 sm:py-4 bg-white text-black text-sm sm:text-base font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
          >
            お問い合わせはこちら
          </a>
        </div>
      </div>
    </div>
  );
}
